import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { projects, testimonials } from "../data/work";
import { Reveal } from "./motion";
import { useI18n } from "../i18n";

// Device mockups ---------------------------------------------------------

const BrowserFrame = ({ image, domain, alt }) => (
  <div className="overflow-hidden rounded-t-xl border border-white/10 bg-[#1b1b1f] shadow-2xl shadow-black/40">
    <div className="flex items-center gap-2 border-b border-white/10 px-3.5 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
      <span className="ml-3 truncate rounded-md bg-white/10 px-3 py-1 font-mono text-[0.6875rem] text-white/70">{domain}</span>
    </div>
    <img src={image} alt={alt} loading="lazy" className="block aspect-[16/10] w-full object-cover object-top" />
  </div>
);

const PhoneFrame = ({ image, alt }) => (
  <div className="relative mx-auto w-[200px] rounded-[2.2rem] border-[7px] border-[#111] bg-[#111] shadow-2xl shadow-black/40">
    <span className="absolute left-1/2 top-2 z-10 h-4 w-16 -translate-x-1/2 rounded-full bg-[#111]" />
    <img src={image} alt={alt} loading="lazy" className="block aspect-[1/2] w-full rounded-[1.7rem] object-cover object-top" />
  </div>
);

const WindowFrame = ({ image, alt }) => (
  <img
    src={image}
    alt={alt}
    loading="lazy"
    className="mx-auto block aspect-square w-full max-w-[340px] rounded-2xl object-cover shadow-2xl shadow-black/30"
  />
);

// Project tile -----------------------------------------------------------

const Tile = ({ project, className = "", layout = "stack" }) => {
  const { t } = useI18n();
  const { tone } = project;
  const kind = t(`work.${project.id}.kind`);
  const summary = t(`work.${project.id}.summary`);
  const highlights = t(`work.${project.id}.highlights`);
  const alt = `${project.name} website`;
  const mock =
    project.frame === "browser" ? (
      <BrowserFrame image={project.image} domain={project.domain} alt={alt} />
    ) : project.frame === "phone" ? (
      <PhoneFrame image={project.image} alt={alt} />
    ) : (
      <WindowFrame image={project.image} alt={alt} />
    );

  const text = (
    <div>
      <div className="flex items-center justify-between gap-4">
        <span className="font-mono text-[0.6875rem] uppercase tracking-[0.14em]" style={{ color: tone.muted }}>
          {kind}
        </span>
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors duration-300"
          style={{ borderColor: `${tone.fg}33` }}
        >
          <ArrowUpRight
            size={16}
            className="transition-transform duration-300 motion-safe:group-hover:-translate-y-0.5 motion-safe:group-hover:translate-x-0.5"
          />
        </span>
      </div>
      <h3 className="mt-4 font-display text-3xl leading-[1.1] md:text-[2.125rem]">{project.name}</h3>
      <p className="mt-3 max-w-md text-[0.9375rem] leading-relaxed" style={{ color: tone.muted }}>
        {summary}
      </p>
      <ul className="mt-5 flex flex-wrap gap-2">
        {highlights.map((h) => (
          <li
            key={h}
            className="rounded-full px-3 py-1 text-xs font-medium"
            style={{ backgroundColor: `${tone.fg}14`, color: tone.fg }}
          >
            {h}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <a
      href={project.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${project.name}: ${t("home.work.visit")}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-3xl p-7 transition-transform duration-500 hover:-translate-y-1 md:p-9 ${className}`}
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      {/* soft brand glow behind the mockup */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 left-1/2 h-72 w-[80%] -translate-x-1/2 rounded-full opacity-40 blur-3xl transition-opacity duration-500 group-hover:opacity-60"
        style={{ backgroundColor: tone.accent }}
      />
      {layout === "split" ? (
        <div className="relative grid flex-grow gap-10 lg:grid-cols-5 lg:items-end">
          <div className="lg:col-span-2 lg:self-start">{text}</div>
          <div className="-mb-7 md:-mb-9 lg:col-span-3 lg:-mr-9 transition-transform duration-500 motion-safe:group-hover:-translate-y-2">
            {mock}
          </div>
        </div>
      ) : (
        <div className="relative flex flex-grow flex-col">
          {text}
          <div
            className={`mt-9 flex-grow transition-transform duration-500 motion-safe:group-hover:-translate-y-2 ${
              project.frame === "phone" ? "-mb-44 md:-mb-48" : ""
            }`}
          >
            {mock}
          </div>
        </div>
      )}
    </a>
  );
};

// Sections ---------------------------------------------------------------

export const WorkShowcase = () => {
  const { t } = useI18n();
  const [feature, second, ...rest] = projects;
  return (
    <div className="grid gap-5 lg:grid-cols-12">
      <Reveal className="lg:col-span-8">
        <Tile project={feature} layout="split" />
      </Reveal>
      <Reveal className="lg:col-span-4" delay={120}>
        <Tile project={second} />
      </Reveal>
      {rest.map((p, i) => (
        <Reveal key={p.id} className="lg:col-span-4" delay={i * 120}>
          <Tile project={p} className="max-h-[640px]" />
        </Reveal>
      ))}
      <Reveal className="lg:col-span-4" delay={240}>
        <Link
          to="/contact"
          className="group flex h-full min-h-[320px] flex-col justify-between rounded-3xl border border-dashed border-ink/25 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand hover:bg-brand-soft/60 md:p-9"
        >
          <span className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-ink-3">{t("home.work.nextLabel")}</span>
          <div className="mt-16">
            <h3 className="font-display text-4xl leading-[1.1] text-ink">{t("home.work.nextTitle")}</h3>
            <p className="mt-3 max-w-xs text-[0.9375rem] leading-relaxed text-ink-2">{t("home.work.nextBody")}</p>
            <span className="btn btn-primary btn-sm mt-8">
              {t("home.work.nextCta")}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
            </span>
          </div>
        </Link>
      </Reveal>
    </div>
  );
};

const initials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Renders nothing until real quotes are added to src/data/work.js.
export const Testimonials = () => {
  if (!testimonials.length) return null;
  const [lead, ...others] = testimonials;
  const projectFor = (t) => projects.find((p) => p.id === t.projectId);

  return (
    <div className="grid gap-5 lg:grid-cols-12">
      <figure className="relative overflow-hidden rounded-3xl bg-deep p-8 text-white md:p-12 lg:col-span-8">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-4 -top-16 select-none font-display text-[16rem] leading-none text-white/[0.06]"
        >
          ”
        </span>
        <blockquote className="relative font-display text-3xl leading-[1.25] md:text-[2.5rem]">“{lead.quote}”</blockquote>
        <figcaption className="relative mt-10 flex flex-wrap items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-sm font-semibold">
            {initials(lead.name)}
          </span>
          <span>
            <span className="block font-medium">{lead.name}</span>
            <span className="block text-sm text-[#b8c9c6]">{lead.role}</span>
          </span>
          {projectFor(lead) && (
            <a
              href={projectFor(lead).url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 hover:border-white/50"
            >
              See the project ↗
            </a>
          )}
        </figcaption>
      </figure>
      {others.map((t) => (
        <figure key={t.name} className="flex flex-col justify-between rounded-3xl border border-line bg-surface p-8 lg:col-span-4">
          <blockquote className="text-lg leading-relaxed text-ink">“{t.quote}”</blockquote>
          <figcaption className="mt-8 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
              {initials(t.name)}
            </span>
            <span>
              <span className="block text-sm font-medium text-ink">{t.name}</span>
              <span className="block text-sm text-ink-3">{t.role}</span>
            </span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
};
